import { fromPrivateKey, generateKeyPair, type KeyPair } from '../crypto/keys.js';
import { bytesToHex, hexToBytes } from '../util/binary.js';

const WALLET_KEY = 'mloby:wallet:v1';

interface StoredWallet {
  v: 1;
  privateKeyHex: string;
}

/**
 * Load the user's wallet from localStorage. If none exists, generate a fresh
 * keypair, persist it, and return that. Idempotent — calling this on every
 * page load is the intended pattern.
 */
export function loadOrCreateWallet(): KeyPair {
  const existing = localStorage.getItem(WALLET_KEY);
  if (existing) {
    try {
      const parsed = JSON.parse(existing) as StoredWallet;
      if (parsed.v === 1 && parsed.privateKeyHex) {
        return fromPrivateKey(hexToBytes(parsed.privateKeyHex));
      }
    } catch {
      // fall through and regenerate — corruption shouldn't brick the wallet
    }
  }
  const kp = generateKeyPair();
  saveWallet(kp);
  return kp;
}

/** True when a valid wallet is already stored in this browser (never creates one). */
export function hasStoredWallet(): boolean {
  try {
    const raw = localStorage.getItem(WALLET_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as StoredWallet;
    return parsed.v === 1 && /^[0-9a-f]{64}$/i.test(parsed.privateKeyHex ?? '');
  } catch {
    return false;
  }
}

/** Import from a bare 64-hex private key. Throws on bad input; saves on success. */
export function importPrivateKeyHex(hex: string): KeyPair {
  const clean = hex.trim().replace(/^0x/i, '');
  if (!/^[0-9a-fA-F]{64}$/.test(clean)) throw new Error('Private key must be 64 hex characters.');
  const kp = fromPrivateKey(hexToBytes(clean.toLowerCase()));
  saveWallet(kp);
  return kp;
}

export function saveWallet(kp: KeyPair): void {
  const data: StoredWallet = { v: 1, privateKeyHex: bytesToHex(kp.privateKey) };
  localStorage.setItem(WALLET_KEY, JSON.stringify(data));
}

const WALLET_FILE_TYPE = 'mloby-wallet';

/** Serialize the wallet for export as a downloadable JSON file. */
export function exportWalletJson(kp: KeyPair): string {
  const out = {
    type: WALLET_FILE_TYPE,
    version: 1,
    address: kp.address,
    privateKeyHex: bytesToHex(kp.privateKey),
    warning: 'Anyone with the private key controls the wallet. Keep this safe.',
  };
  return JSON.stringify(out, null, 2);
}

/** Import from the file format produced by exportWalletJson. Throws on bad input. */
export function importWalletJson(text: string): KeyPair {
  const parsed = JSON.parse(text) as { type?: string; privateKeyHex?: string };
  if (parsed.type !== WALLET_FILE_TYPE) throw new Error('not a mloby wallet file');
  if (!parsed.privateKeyHex) throw new Error('missing privateKeyHex');
  const kp = fromPrivateKey(hexToBytes(parsed.privateKeyHex));
  saveWallet(kp);
  return kp;
}

/** Wipe the wallet — irreversible without an exported backup. */
export function deleteWallet(): void {
  localStorage.removeItem(WALLET_KEY);
}
